import { logger } from "src/core/globals";
import { Error, Message } from "src/gen/schema/syncfollow/syncfollow";
import { ReST } from "src/utils/api/rest/rest";

import { KEEP_ALIVE_MSG, MAX_COOLDOWN } from "./core/constants";
import { Socket } from "./core/socket";
import { Payload } from "./core/types";
import { BinaryTransport, StringTransport } from "./syncFollowTransport";

/**
 * After this many consecutive WebSocket failures, fire a lightweight REST
 * probe to re-discover the correct API origin. Set to 1 so recovery from
 * a cell-pair switchover or wrong-region login happens on the first retry.
 */
const RECONNECT_PROBE_THRESHOLD = 1;

const isKeepAlive = (msg: Message): boolean => {
  return msg.echoResponse?.message.includes(KEEP_ALIVE_MSG) || false;
};

class SyncFollowSocket extends Socket<Message> {
  private transport: BinaryTransport | StringTransport = new BinaryTransport();
  protected consecutiveFailures = 0;

  constructor() {
    super();
    this.on(
      Socket.ERROR,
      (e: Error) => {
        if (e.httpStatusCode !== 404) {
          logger.error(e);
        }
      },
      this
    );
  }

  init(url: string | (() => string)) {
    const useBinary = window.cnc.settings.get("useBinarySyncFollow");
    this.transport = useBinary ? new BinaryTransport() : new StringTransport();
    this.binaryType = useBinary ? "arraybuffer" : "blob";
    super.init(url);
  }

  getKeepAliveMessage(): Message {
    return Message.create({
      echoRequest: {
        numResponses: 1,
        message: KEEP_ALIVE_MSG,
        waitSecs: 0,
        requestTs: new Date()
      }
    });
  }

  broadcast(msg: Message) {
    if (!isKeepAlive(msg)) {
      super.broadcast(msg);
    }
  }

  onOpen(): void {
    super.onOpen();
    this.consecutiveFailures = 0;
  }

  onClose(url: string | (() => string), e: CloseEvent) {
    this.consecutiveFailures++;
    if (this.reconnect) {
      if (this.consecutiveFailures >= RECONNECT_PROBE_THRESHOLD) {
        // Use an absolute page-origin URL so the request always goes through
        // Fred (bypassing any stale activeApiOrigin). The REST layer handles
        // origin re-discovery from 307 redirects, 401 → logout, and logging.
        ReST.get(`${window.location.origin}/ui/v1/sessions`).catch(() => {
          /* best-effort probe */
        });
      }
    }
    super.onClose(url, e);
  }

  onMessage(m: MessageEvent<string | ArrayBuffer>) {
    const msg: Message = this.decode(m);
    if (msg.error) {
      this.fireEvent(Socket.ERROR, msg.error);
      if (msg.error.fatalError) {
        this.cooldown = Math.min(
          this.cooldown + msg.error.backoffTimeMs,
          MAX_COOLDOWN
        );
      }
    }
    /**
     * If we get any message on the socket
     * after we sent the ping we can clear
     * the ping timeout. This is just to
     * be sure if we send a ping and get
     * no response we close the socket
     * and reconnect.
     */
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    this.broadcast(msg);
  }

  decode(m: MessageEvent<string | ArrayBuffer>): Message {
    return this.transport.decode(m.data);
  }

  encode(msg: Message): Payload {
    return this.transport.encode(msg);
  }
}

export { SyncFollowSocket };
